import React, { useEffect, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';

import fileStackClient from '@app/filestack';
import {
  PickerOptions,
  PickerResponse,
  PickerInstance,
} from 'filestack-js/build/main/lib/picker';

import { useBreakpoint } from 'hooks';
import { AuthContext } from 'contexts';
import COVID19SettingsContainer from '@app/covid19/COVID19SettingsContainer';
import API from 'api';

import { Page, Button, Spin, JumpTo, Modal, Space, message } from 'components';
import { SetOrgSettingsDTO } from '@bb/domain/dist/types';
import { isValidUrl } from 'utils';
import { StyledLayout, StyledLoaderWrapper } from './StyledComponents';

import { General, OpenHouse, OfferNotification } from './SettingSections';

import { handleChangeSettingType } from './types';

type OpenFilePicker = (editUrl?: string, type?: 'branch' | 'global') => void;

const OrgSettings: React.FC = () => {
  // get auth
  const { auth, setAuth, hasFeature } = useContext(AuthContext);

  const history = useHistory();

  const [isChangedOfferSettings, setIsChangedOfferSettings] =
    React.useState(false);

  const [settings, setSettings] = React.useState<SetOrgSettingsDTO>({
    enableOpenHouses: false,
    logo: '',
    branchLogo: '',
  });

  const [loading, setLoading] = React.useState(true);
  const [prevLogo, setPrevLogo] = React.useState(auth.org.logoUrl || '');
  const [pickerInstance, setPickerInstance] = useState<PickerInstance | null>(
    null,
  );

  const [prevBranchLogo, setPrevBranchLogo] = React.useState(
    auth.org.locations[+auth.location]?.logo || '',
  );
  useEffect(() => {
    setSettings(prevSettings => ({
      ...prevSettings,
      logo: auth.org?.logoUrl || '',
      branchLogo: auth.org.locations[+auth.location].logo || '',
    }));
  }, [auth]);

  useEffect(() => () => {
      if (pickerInstance && typeof pickerInstance.close === 'function')
        pickerInstance.close();
    }, [history, pickerInstance]);

  const saveOfferSettingsModal = () => Modal.info({
      width: 480,
      title: 'Applying offer notification settings to users and listings',
      content: (
        <Space direction="vertical" align="center" size="large">
          Depending on the number of users and listings, the saving process may
          take up to 3 minutes.
          <Spin size="large" />
        </Space>
      ),
      closable: false,
      keyboard: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
    });

  const handleError = (err: any) => {
    errorReporter.error(err);
    Swal.fire({
      title: 'Server Error',
      text: 'Please try again later',
      type: 'error',
    }).then(() => {
      window.location.href = '/';
    });
  };

  const handleSave = async (
    newSettings: Partial<SetOrgSettingsDTO>,
    cb?: () => void,
  ) => {
    let modal;
    if (isChangedOfferSettings) {
      modal = saveOfferSettingsModal();
    }

    try {
      await API.POST.saveOrgSettings(newSettings);
      message.success('Settings saved');
    } catch (err) {
      handleError(err);
    } finally {
      if (cb) cb();
    }

    if (isChangedOfferSettings) {
      setIsChangedOfferSettings(false);
      modal.destroy();
    }
  };

  const isTogglable = (settingKey: keyof SetOrgSettingsDTO) => settingKey === 'enableOpenHouses';

  const handleChangeSettings: handleChangeSettingType = (key, value) => {
    const newSettings = {
      ...settings,
      [key]: isTogglable(key) ? !settings[key] : value,
    };

    setSettings({ ...settings, ...newSettings });

    if (key === 'offerSettings') {
      setIsChangedOfferSettings(true);
    }

    if (isTogglable(key)) {
      // togglable settings should save immediately
      handleSave({ [key]: !settings[key] });
    }
  };

  const openFilePicker: OpenFilePicker = async (editUrl, type) => {
    const processResults = async (results: PickerResponse) => {
      const logoUrl = results.filesUploaded[0].url;
      if (type === 'global' && logoUrl && logoUrl !== settings.logo) {
        const org = cloneDeep(auth.org);
        setPrevLogo(org.logoUrl);
        org.logoUrl = logoUrl;
        setAuth({ ...auth, org });
        handleChangeSettings('logo', logoUrl);
        handleSave({ logo: logoUrl });
      } else if (
        type === 'branch' &&
        logoUrl &&
        logoUrl !== settings.branchLogo
      ) {
        const org = cloneDeep(auth.org);

        setPrevBranchLogo(org.locations[+auth.location].logo || '');

        org.locations[+auth.location].logo = logoUrl;

        setAuth({ ...auth, org });

        handleChangeSettings('branchLogo', logoUrl);

        handleSave({ branchLogo: logoUrl });
      }
    };

    const opts: PickerOptions = {
      accept: 'image/*',
      maxFiles: 1,
      fromSources: ['local_file_system', 'url'],
      onFileSelected: file => {
        if (file.source === 'url' && !isValidUrl(file.originalPath))
          throw new Error('Invalid URL format. Please try again.');
      },
      onUploadDone: processResults,
    };

    const picker = fileStackClient.picker(opts);

    if (editUrl) {
      picker.crop([editUrl]);
      setPickerInstance(picker);
      return;
    }
    setPickerInstance(picker);
    picker.open();
  };

  const onBack = () => {
    history.goBack();
  };

  const handleCancel = () => {
    if (settings.logo) {
      const org = cloneDeep(auth.org);
      org.logoUrl = prevLogo;
      setAuth({ ...auth, org });
    }
    if (settings.branchLogo) {
      const org = cloneDeep(auth.org);
      org.locations[+auth.location].logo = prevBranchLogo;
      setAuth({ ...auth, org });
    }
    onBack();
  };

  const fetchSettings = async () => {
    try {
      const data = await API.GET.getOrgSettings();
      setSettings(prev => ({ ...prev, ...data }));
      setLoading(false);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const { md } = useBreakpoint();

  if (loading) {
    return (
      <StyledLoaderWrapper>
        <Spin />
      </StyledLoaderWrapper>
    );
  }

  return (
    <Page
      title="Brokerage Settings"
      type="hollow"
      fillScreenHeight
      showBackButton
      headerExtra={
        <div>
          <Button onClick={handleCancel} key="cancel">
            Cancel
          </Button>{' '}
          <Button
            type="primary"
            onClick={() => handleSave(settings, onBack)}
            key="save"
          >
            Save
          </Button>
        </div>
      }
      asidePlacement="left"
      asideWidth={170}
      aside={
        md && (
          <JumpTo
            hideBullets
            firstItemAlwaysActive
            items={[
              { label: 'General', id: 'general' },
              hasFeature('COVID_19') && { label: 'COVID-19', id: 'covid-19' },
              { label: 'Open Houses', id: 'openHouses' },
            ].filter(Boolean)}
          />
        )
      }
    >
      <StyledLayout>
        <General
          orgName={auth.org.name}
          branchName={auth.org.locations[+auth.location].name}
          logoUrl={settings.logo}
          branchLogoUrl={settings.branchLogo}
          openFilePicker={openFilePicker}
          title="General"
        />
        {hasFeature('OFFERS') && (
          <OfferNotification
            handleChangeSettings={handleChangeSettings}
            offerSettings={settings.offerSettings}
          />
        )}
        {hasFeature('COVID_19') && (
          <COVID19SettingsContainer
            type="org"
            getCovid19OrgSettings={API.GET.getCovid19OrgSettings}
            saveCovid19OrgSettings={API.POST.saveCovid19OrgSettings}
          />
        )}
        <OpenHouse
          enableOpenHouses={settings.enableOpenHouses}
          handleChangeSettings={handleChangeSettings}
          title="Open Houses"
        />
      </StyledLayout>
    </Page>
  );
};

export default OrgSettings;
